import { Application, utils } from "pixi.js";
import screenfull from "screenfull";
import { DomLayer } from "@cc/html5-dom-layer";
import { SoundManager } from "@cc/soundjs-manager";
import { AssetLoader } from "../loader/AssetLoader";
import { Screen } from "../screen/Screen";
import { DefaultOptionsApp } from "./DefaultOptionsApp";
import { PixiAppOptions } from "./PixiAppOptions";
import { Resizer } from "../resizer/Resizer";
import { ScaleVariant } from "../loader/ScaleVariant";
import { ScreenConfig } from "../screen/ScreenConfig";
import { ScreenManager } from "../screen/ScreenManager";
import { StageBackground } from "./StageBackground";
import { Timeline } from "../animation/Timeline";
import { TimelineOptions } from "../animation/TimelineOptions";
import { TimeoutOptions } from "./TimeoutOptions";
import { Timeout } from "./Timeout";
import { Tween } from "../animation/Tween";
import { TweenOptions } from "../animation/TweenOptions";
import { SpineAnimation } from "../animation/SpineAnimation";
import { StorageManager } from "../storage/StorageManager";

/**
 */
export class PixiApp extends Application {
  /**
   * Should buttons request fullscreen on fullscreen enabled devices?
   */
  public buttonsRequestFullscreen: boolean;

  /**
   * Screen manager instance.
   */
  public screenManager: ScreenManager;

  /**
   * App resizer class instance.
   */
  public resizer: Resizer;

  /**
   */
  public assets: AssetLoader;

  /**
   * Supported scale variants.
   */
  public scaleVariants: ScaleVariant[];

  /**
   */
  public textureScale: number;

  /**
   */
  public domLayer: DomLayer;

  /**
   */
  public sound: SoundManager;

  /**
   */
  public storage: StorageManager;

  /**
   */
  public paused: boolean;

  /**
   */
  public blurred: boolean;

  /**
   */
  public env: string;

  public events: utils.EventEmitter;

  public fullscreenTargetElement?: HTMLElement;

  /**
   */
  public static get fullscreenEnabled() {
    return screenfull && screenfull.enabled;
  }

  /**
   */
  public static get isFullscreen() {
    return screenfull && screenfull.isFullscreen;
  }

  /**
   */
  public requestFullscreen() {
    screenfull && screenfull.request(this.fullscreenTargetElement || this.view);
  }

  public get screens() {
    return this.screenManager;
  }

  public get now(): Screen | undefined {
    if (this.screenManager) return this.screenManager.current;
  }

  /**
   * Solid background color.
   */
  private background: StageBackground;

  /**
   * @param options
   */
  constructor(options: PixiAppOptions) {
    // Merge with default options.
    options = {
      ...DefaultOptionsApp,
      ...options
    };

    //
    super(options as PIXI.ApplicationOptions);
    this.setContextMenuEnabled(options.contextMenuEnabled);
    this.fullscreenTargetElement = options.fullscreenTargetElement;

    if (options.lockScroll) {
      this.preventTouchEvents();
      this.lockScroll();
    }
    this.events = new utils.EventEmitter();
    this.sound = new SoundManager();
    this.resizer = new Resizer(this, options);
    this.screenManager = new ScreenManager(this);
    this.background = new StageBackground(options.backgroundColor);
    this.domLayer = new DomLayer();
    this.assets = new AssetLoader(this);
    this.stage.addChild(this.background);
    this.stage.sortChildren();

    this.resizer.on("resize", (w: number, h: number) => this.resizeMe(w, h));
    this.resizer.update(true);
  }

  public setPause(paused) {
    this.events.emit("pause", paused);
  }

  public setBlur(blurred) {
    this.events.emit("blur", blurred);
  }

  /**
   *
   */
  public showScreen(screenName: string, tags?: any) {
    this.screenManager.showScreen(screenName, tags);
  }

  /**
   */
  public preventTouchEvents() {
    const p = e => e.preventDefault();
    const b = document.body;
    b.addEventListener("touchstart", p, { passive: false });
    b.addEventListener("touchend", p, { passive: false });
    b.addEventListener("touchcancel", p, { passive: false });
    b.addEventListener("touchmove", p, { passive: false });
    document.addEventListener("gesturestart", p);
    window.addEventListener("scroll", p, false);
  }

  /**
   */
  public lockScroll() {
    setInterval(() => {
      if (window.scrollY !== 0) window.scrollTo(0, 0);
    }, 100);
  }

  /**
   * @param scaleVariant
   */
  public setScaleVariant(scaleVariantKey: string) {
    this.assets.setScaleVariant(scaleVariantKey);
  }

  /**
   *
   * @param screens
   */
  public addScreens(screens: ScreenConfig[]) {
    this.screenManager.addScreens(screens);
  }

  /**
   * Create a Timeout class instance.
   */
  public timeout(options?: TimeoutOptions): Timeout {
    options = options || ({} as TimeoutOptions);
    options.group = typeof options.group === "undefined" ? "main" : options.group;
    return new Timeout(options);
  }

  /**
   * Create a createjs tween.
   * @param options
   */
  public tween(target: any, options?: TweenOptions): Tween {
    options = options || ({} as TweenOptions);
    options.group = typeof options.group === "undefined" ? "main" : options.group;
    return Tween.get(target, options);
  }

  /**
   * Create a createjs timeline.
   * @param options
   */
  public timeline(options?: TimelineOptions): Timeline {
    options = options || ({} as TimelineOptions);
    options.group = typeof options.group === "undefined" ? "main" : options.group;
    return new Timeline(options);
  }

  /**
   *
   */
  public removeTweens(target) {
    Tween.removeTweens(target);
  }

  /**
   *
   * @param width
   * @param height
   */
  public resizeMe(width: number, height: number) {
    this.screenManager.resize(width, height);
    this.domLayer.resize(width, height);
    this.background.resize(width, height);
  }

  /**
   * @param name
   */
  public getSpineData(name: string): PIXI.spine.core.SkeletonData | undefined {
    return SpineAnimation.getResource(name);
  }

  /**
   *
   * @param enabled
   */
  public setContextMenuEnabled(enabled: boolean) {
    if (!enabled) this.renderer.view.addEventListener("contextmenu", this.onContextMenu);
    else this.renderer.view.removeEventListener("contextmenu", this.onContextMenu);
  }

  /**
   *
   * @param event
   */
  private onContextMenu(event: any) {
    event.preventDefault();
  }
}
